import React, { useState, useCallback } from "react";
import { X, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DATA_SOURCES, BIASES, BEHAVIOUR_PROFILES } from "@/data/cardGameData";
import { getCardDisplayTitle } from "@/views/cardgame/cardDisplay";

const ALL_CARDS = {};
[...DATA_SOURCES, ...BIASES, ...BEHAVIOUR_PROFILES].forEach((c) => {
  ALL_CARDS[c.id] = c;
});

const copy = {
  nl: {
    dataSource: "Databron",
    dataSources: "Databronnen",
    bias: "Bias",
    responseMode: "Responsmodus",
    cardNotFound: "Kaart niet gevonden",
    noCardWithId: (id) => `Geen kaart met ID "${id}"`,
    alreadyAdded: "Al toegevoegd",
    oneModeOnly: "Er is maar een responsmodus toegestaan",
    removeMode: "Verwijder de huidige responsmodus om een andere te kiezen.",
    added: (title) => `Toegevoegd: ${title}`,
    generating: "Outputs genereren...",
    generatingText: "De synthetische gebruikers worden opgebouwd vanuit de kaarten die je koos.",
    roundChoose: (round) => `Ronde ${round}, kies je kaarten`,
    title: "Voer je kaarten in",
    instruction: "Typ de code op elke kaart, bijvoorbeeld BI-01, om deze aan de ronde toe te voegen.",
    dataSourcesCount: "Databronnen",
    biasCount: "Bias",
    responseModeCount: "Responsmodus",
    lastAdded: "Laatst toegevoegd",
    chosenCards: "Gekozen kaarten",
    addByCode: "Kaart toevoegen via code",
    placeholder: "Voer kaartcode in, bijv. BI-01",
    add: "Toevoegen",
    startRound2: "Start ronde 2 ->",
    generateBoth: "Genereer beide outputs ->",
    missingSelection: "Voeg minimaal 1 databron, 1 bias en 1 responsmodus toe",
  },
  en: {
    dataSource: "Data Source",
    dataSources: "Data Sources",
    bias: "Bias",
    responseMode: "Response mode",
    cardNotFound: "Card not found",
    noCardWithId: (id) => `No card with ID "${id}"`,
    alreadyAdded: "Already added",
    oneModeOnly: "Only one response mode allowed",
    removeMode: "Remove the current response mode to choose a different one.",
    added: (title) => `Added: ${title}`,
    generating: "Generating outputs...",
    generatingText: "Building your synthetic users from the cards you chose.",
    roundChoose: (round) => `Round ${round}, choose your cards`,
    title: "Enter your cards",
    instruction: "Type the code printed on each card, for example BI-01, to add it to this round.",
    dataSourcesCount: "Data Sources",
    biasCount: "Bias",
    responseModeCount: "Response mode",
    lastAdded: "Last added",
    chosenCards: "Chosen cards",
    addByCode: "Add a card by code",
    placeholder: "Enter card code, e.g. BI-01",
    add: "Add",
    startRound2: "Start Round 2 ->",
    generateBoth: "Generate both outputs ->",
    missingSelection: "Add at least 1 Data Source, 1 Bias, and 1 response mode",
  },
};

function getCategoryColor(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return "#5803FD";
  if (BIASES.find((c) => c.id === id)) return "#e85e0b";
  return "#63cba4";
}

function getCategoryLabel(id, t) {
  if (DATA_SOURCES.find((c) => c.id === id)) return t.dataSource;
  if (BIASES.find((c) => c.id === id)) return t.bias;
  return t.responseMode;
}

const isBehaviour = (id) => !!BEHAVIOUR_PROFILES.find((p) => p.id === id);

function getCategoryOrder(id) {
  if (DATA_SOURCES.find((c) => c.id === id)) return 0;
  if (BIASES.find((c) => c.id === id)) return 1;
  return 2;
}

function sortCardsByCategory(cards) {
  return [...cards].sort((a, b) => getCategoryOrder(a.id) - getCategoryOrder(b.id));
}

export function QRScannerStep({ round, scannedCards, onCardScanned, onDone, loading, language = "nl" }) {
  const [lastScanned, setLastScanned] = useState(null);
  const [manualInput, setManualInput] = useState("");
  const t = copy[language] || copy.nl;

  const handleManualAdd = useCallback(() => {
    const id = manualInput.trim().toLowerCase();
    if (!id) return;
    const card = ALL_CARDS[id];
    if (!card) {
      toast.error(t.cardNotFound, { description: t.noCardWithId(id) });
      return;
    }
    if (scannedCards.find((c) => c.id === id)) {
      toast(t.alreadyAdded, { description: getCardDisplayTitle(card, language) });
      return;
    }
    if (isBehaviour(id) && scannedCards.some((c) => isBehaviour(c.id))) {
      toast.error(t.oneModeOnly, { description: t.removeMode });
      return;
    }
    onCardScanned(card);
    setLastScanned(card);
    setManualInput("");
    toast.success(t.added(getCardDisplayTitle(card, language)));
  }, [manualInput, scannedCards, onCardScanned, t, language]);

  const dataSources = scannedCards.filter((c) => DATA_SOURCES.find((d) => d.id === c.id));
  const biases = scannedCards.filter((c) => BIASES.find((b) => b.id === c.id));
  const profiles = scannedCards.find((c) => BEHAVIOUR_PROFILES.find((p) => p.id === c.id));
  const canFinish = dataSources.length >= 1 && biases.length >= 1 && !!profiles;
  const sortedCards = sortCardsByCategory(scannedCards);

  if (loading) {
    return (
      <div className="rounded-2xl border border-black/10 bg-white p-10 text-center">
        <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#905cff] mb-4" />
        <div className="font-display text-lg text-black">{t.generating}</div>
        <div className="text-sm text-black/50 mt-1">{t.generatingText}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="text-[11px] uppercase tracking-wider font-medium mb-1" style={{ color: round === 1 ? "#5803FD" : "#e85e0b" }}>
          {t.roundChoose(round)}
        </div>
        <h2 className="font-display text-xl font-semibold text-black">{t.title}</h2>
        <p className="mt-2 text-sm text-black/60 leading-relaxed">
          {t.instruction}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="rounded-full border border-[#5803FD]/30 bg-[#5803FD]/5 px-2.5 py-1 text-xs text-[#5803FD]">
            {t.dataSourcesCount}: {dataSources.length}/2
          </span>
          <span className="rounded-full border border-[#e85e0b]/30 bg-[#e85e0b]/5 px-2.5 py-1 text-xs text-[#e85e0b]">
            {t.biasCount}: {biases.length}/2
          </span>
          <span className="rounded-full border border-[#63cba4]/30 bg-[#63cba4]/5 px-2.5 py-1 text-xs text-[#63cba4]">
            {t.responseModeCount}: {profiles ? "1/1" : "0/1"}
          </span>
        </div>
      </div>

      {lastScanned && (
        <div className="rounded-2xl border-2 border-[#63cba4] bg-[rgba(99,203,164,0.08)] p-4 flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-[#63cba4] shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#905cff]">{t.lastAdded}</div>
            <div className="font-medium text-sm text-black">{getCardDisplayTitle(lastScanned, language)}</div>
            <div className="text-xs text-black/50">{getCategoryLabel(lastScanned.id, t)}</div>
          </div>
        </div>
      )}

      {scannedCards.length > 0 && (
        <div className="rounded-2xl border border-black/10 bg-white p-4">
          <div className="text-xs text-black/40 uppercase tracking-wider mb-3">{t.chosenCards}</div>
          <div className="space-y-2">
            {sortedCards.map((card) => (
              <div key={card.id} className="flex items-center gap-3 rounded-xl border border-black/8 p-3">
                <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: getCategoryColor(card.id) }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-black truncate">{getCardDisplayTitle(card, language)}</div>
                  <div className="text-xs text-black/40">{getCategoryLabel(card.id, t)}</div>
                </div>
                <button
                  onClick={() => onCardScanned({ ...card, _remove: true })}
                  className="text-black/30 hover:text-red-400 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-black/10 bg-white p-4">
        <div className="text-xs text-black/40 uppercase tracking-wider mb-2">{t.addByCode}</div>
        <div className="flex gap-2">
          <input
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleManualAdd()}
            placeholder={t.placeholder}
            className="flex-1 rounded-xl border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#905cff]/40"
          />
          <Button onClick={handleManualAdd} variant="outline" className="rounded-xl">{t.add}</Button>
        </div>
      </div>

      <Button
        onClick={onDone}
        disabled={!canFinish}
        className="w-full rounded-xl bg-[#905cff] text-white hover:bg-[#5802ff] disabled:opacity-50 py-3"
      >
        {canFinish
          ? round === 1
            ? t.startRound2
            : t.generateBoth
          : t.missingSelection}
      </Button>
    </div>
  );
}
